import { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetPackCategories } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IPackCategory, PackCategory } from '../../../@types/model/masterData/packCategory/packCategory';
import PackCategoryHttpService from '../../../services/http/masterData/packCategoryHttpService';
import { IPackCategoryFormValues, PackCategoryFormValues } from '../../../@types/model/masterData/packCategory/packCategoryFormValues';
import PackCategoryForm from './form/PackCategoryForm';
import { useHistory } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { setPackCategoryMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';

const PackCategoryScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const packCategories = useAppSelector(x => x.masterData.packCategories);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedPackCategory, setSelectedPackCategory] = useState<IPackCategory>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingPackCategory, setDeletingPackCategory] = useState<IPackCategory>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await PackCategoryHttpService.getPackCategoryData();
                    dispatch(dataSetPackCategories(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading pack categories.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedPackCategory(undefined);
        setFormGuid(undefined);
    };

    const editPackCategory = (packCategory : IPackCategory) => {
        setSelectedPackCategory(packCategory);
        setFormGuid(packCategory.guid);
        openDialog();
    };

    const onSubmit = async (values : IPackCategoryFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await PackCategoryHttpService.addOrUpdatePackCategory(PackCategory(payload));
            const updatedList = upsertArrayElement(packCategories, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPackCategories(updatedList));
            await setPackCategoryMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPackCategory ? 'Pack Category updated successfully.' : 'Pack Category added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating pack category data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (packCategory : IPackCategory) => {
        setDeletingPackCategory(packCategory);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingPackCategory(undefined);
        setIsDeletePopupOpen(false);
    };

    const removePackCategory = async () => {
        if (!deletingPackCategory) return;
        const updatedPackCategory = { ...deletingPackCategory, isActive: false };
        setIsLoading(true);
        try {
            const res = await PackCategoryHttpService.deletePackCategory(updatedPackCategory.id);
            const updatedList = upsertArrayElement(packCategories, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPackCategories(updatedList));
            await setPackCategoryMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Pack Category deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting pack category.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return PackCategoryFormValues(selectedPackCategory);
        }
    }, [selectedPackCategory, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Pack Category'
                    isEdit={!!selectedPackCategory}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <PackCategoryForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IPackCategory>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editPackCategory}
                        enableEditing={hasEditingRight}
                        enableDeleting={pc => pc.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Category', field: 'category', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={packCategories}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Pack Category'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this pack category?'
                onAccept={removePackCategory}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PackCategoryScreen;
